import { useState } from 'react';

/**
 * Custom hook for managing LMS form states
 * Centralizes all form state management for courses, modules, lessons, and quizzes
 */
export const useLMSForms = () => {
  // Form visibility states
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [showQuizForm, setShowQuizForm] = useState(false);

  // Currently editing items
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);

  // Course Form State
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    abstract: '',
    web_enrichment_enabled: false,
    primary_categories: [],
    tier: 'tier_1',
    thumbnail_url: '',
    instructor: '',
    duration_minutes: '',
    order: 0,
    is_published: true,
    visibility: 'public',
    access_code_required: false,
    themes: [],
    ad_enabled: false,
    ad_title: '',
    ad_content: '',
    ad_image_url: '',
    ad_link_url: '',
    ad_link_text: ''
  });

  // Module Form State
  const [moduleForm, setModuleForm] = useState({
    course_id: '',
    title: '',
    description: '',
    content_sections: [],
    order: 0,
    support_photos: [],
    support_videos: [],
    support_links: [],
    support_downloads: [],
    ad_enabled: false,
    ad_title: '',
    ad_content: '',
    ad_image_url: '',
    ad_link_url: '',
    ad_link_text: ''
  });

  // Lesson Form State
  const [lessonForm, setLessonForm] = useState({
    module_id: '',
    title: '',
    description: '',
    content_sections: [],
    content_type: 'video',
    video_url: '',
    written_content: '',
    pdf_url: '',
    markdown_content: '',
    duration_minutes: '',
    order: 0,
    support_photos: [],
    support_videos: [],
    support_links: [],
    support_downloads: [],
    ad_enabled: false,
    ad_title: '',
    ad_content: '',
    ad_image_url: '',
    ad_link_url: '',
    ad_link_text: '',
    ad_position: 'bottom'
  });

  // Quiz Form State
  const [quizForm, setQuizForm] = useState({
    lesson_id: '',
    title: '',
    description: '',
    passing_score: 70,
    questions: []
  });

  // Module Content Section Form (for module overview content)
  const [moduleContentSectionForm, setModuleContentSectionForm] = useState({
    title: '',
    type: 'markdown',
    order: 0,
    video_url: '',
    text_content: '',
    markdown_content: '',
    pdf_url: '',
    presentation_url: ''
  });

  const [editingModuleSectionId, setEditingModuleSectionId] = useState(null);

  // New question form for quizzes
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    type: 'multiple_choice',
    options: ['', '', '', ''],
    correct_answer: '',
    explanation: ''
  });

  // Reset course form
  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      abstract: '',
      web_enrichment_enabled: false,
      primary_categories: [],
      tier: 'tier_1',
      thumbnail_url: '',
      instructor: '',
      duration_minutes: '',
      order: 0,
      is_published: true,
      access_code_required: false,
      themes: [],
      ad_enabled: false,
      ad_title: '',
      ad_content: '',
      ad_image_url: '',
      ad_link_url: '',
      ad_link_text: ''
    });
    setEditingCourse(null);
    setShowCourseForm(false);
  };

  // Reset module form
  const resetModuleForm = () => {
    setModuleForm({
      course_id: '',
      title: '',
      description: '',
      content_sections: [],
      order: 0,
      support_photos: [],
      support_videos: [],
      support_links: [],
      support_downloads: [],
      ad_enabled: false,
      ad_title: '',
      ad_content: '',
      ad_image_url: '',
      ad_link_url: '',
      ad_link_text: ''
    });
    setEditingModule(null);
    setShowModuleForm(false);
  };

  // Reset lesson form
  const resetLessonForm = () => {
    setLessonForm({
      module_id: '',
      title: '',
      description: '',
      content_sections: [],
      content_type: 'video',
      video_url: '',
      written_content: '',
      pdf_url: '',
      markdown_content: '',
      duration_minutes: '',
      order: 0,
      support_photos: [],
      support_videos: [],
      support_links: [],
      support_downloads: [],
      ad_enabled: false,
      ad_title: '',
      ad_content: '',
      ad_image_url: '',
      ad_link_url: '',
      ad_link_text: '',
      ad_position: 'bottom'
    });
    setEditingLesson(null);
    setShowLessonForm(false);
  };

  // Reset quiz form
  const resetQuizForm = () => {
    setQuizForm({
      lesson_id: '',
      title: '',
      description: '',
      passing_score: 70,
      questions: []
    });
    setShowQuizForm(false);
  };

  return {
    // Form visibility
    showCourseForm,
    setShowCourseForm,
    showModuleForm,
    setShowModuleForm,
    showLessonForm,
    setShowLessonForm,
    showQuizForm,
    setShowQuizForm,

    // Editing states
    editingCourse,
    setEditingCourse,
    editingModule,
    setEditingModule,
    editingLesson,
    setEditingLesson,

    // Form states
    courseForm,
    setCourseForm,
    moduleForm,
    setModuleForm,
    lessonForm,
    setLessonForm,
    quizForm,
    setQuizForm,
    moduleContentSectionForm,
    setModuleContentSectionForm,
    editingModuleSectionId,
    setEditingModuleSectionId,
    newQuestion,
    setNewQuestion,

    // Reset functions
    resetCourseForm,
    resetModuleForm,
    resetLessonForm,
    resetQuizForm
  };
};
